/**
 * A publisher account may name the one agency (partner) of its business unit allowed to use it.
 * The create, edit and detail screens share these pieces so they read the same partner list and
 * send the same `partnerId` contract: absent means internal-only, and `null` on update clears it.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import type { Control, FieldValues, UseFormSetValue } from 'react-hook-form';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { EnrichmentSkeleton } from 'src/components/common/EnrichmentSkeleton';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import type { PartnerDto } from 'src/store/generated/leela-api-generated';
import { useBusinessUnitAdminControllerFindPartnersQuery } from 'src/store/leela-api';

export const PARTNER_FIELD_NAME = 'partnerId';
const NO_PARTNER_VALUE = '';
const FORBIDDEN_STATUS = 403;

const isForbiddenError = (error: unknown): boolean =>
  (error as { status?: unknown } | undefined)?.status === FORBIDDEN_STATUS;

function useBusinessUnitPartners (businessUnitId: string | undefined, shouldSkip = false) {
  const { data, isLoading, isError, error } = useBusinessUnitAdminControllerFindPartnersQuery(
    { id: businessUnitId ?? '', page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
    { skip: !businessUnitId || shouldSkip },
  );
  const partners = useMemo(() => (data?.data ?? []) as PartnerDto[], [data]);
  return { partners, isLoading, isError, isForbidden: isForbiddenError(error) };
}

interface PublisherAccountPartnerSelectProps {
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  businessUnitId?: string;
}

export const PublisherAccountPartnerSelect: React.FC<PublisherAccountPartnerSelectProps> = ({ control, setValue, businessUnitId }) => {
  const { t } = useTranslation();
  const { partners, isLoading, isError, isForbidden } = useBusinessUnitPartners(businessUnitId);
  const options = useMemo(
    () => [
      { label: t('publisher_config_agency_none'), value: NO_PARTNER_VALUE },
      ...partners.map(partner => ({ label: partner.name, value: partner.id })),
    ],
    [partners, t],
  );

  // A partner belongs to one business unit, so a different unit invalidates the selection.
  const previousBusinessUnitId = useRef(businessUnitId);
  useEffect(() => {
    if (previousBusinessUnitId.current !== businessUnitId) {
      previousBusinessUnitId.current = businessUnitId;
      setValue(PARTNER_FIELD_NAME, NO_PARTNER_VALUE);
    }
  }, [businessUnitId, setValue]);

  if (!businessUnitId) return null;

  const errorHelperText = isForbidden ? t('publisher_config_agency_forbidden') : t('publisher_config_agency_load_error');

  return (
    <FormSelect
      control={control}
      data-testid="publisher-config-agency-selector"
      disabled={isError}
      helperText={isError ? errorHelperText : undefined}
      label={t('publisher_config_agency')}
      loading={isLoading}
      name={PARTNER_FIELD_NAME}
      options={options}
    />
  );
};

interface PublisherAccountPartnerNameProps {
  businessUnitId?: string;
  partnerId?: string | null;
}

/** Read-only agency name; falls back to the stored id when the partner list is unavailable. */
export const PublisherAccountPartnerName: React.FC<PublisherAccountPartnerNameProps> = ({ businessUnitId, partnerId }) => {
  const { t } = useTranslation();
  const { partners, isLoading } = useBusinessUnitPartners(businessUnitId, !partnerId);

  if (!partnerId) return <Text data-testid="publisher-config-agency">{t('publisher_config_agency_none')}</Text>;
  if (isLoading) return <EnrichmentSkeleton />;
  return <Text data-testid="publisher-config-agency">{partners.find(partner => partner.id === partnerId)?.name ?? partnerId}</Text>;
};

/** The create payload's `partnerId`: sent only when an agency is chosen. */
export function buildCreatePartnerId (selectedPartnerId: unknown): { partnerId?: string } {
  return typeof selectedPartnerId === 'string' && selectedPartnerId !== NO_PARTNER_VALUE
    ? { partnerId: selectedPartnerId }
    : {};
}

/** The update payload's `partnerId`: omitted when unchanged, `null` when cleared, else the new agency. */
export function buildUpdatePartnerId (storedPartnerId: string | null | undefined, selectedPartnerId: unknown): { partnerId?: string | null } {
  const selected = typeof selectedPartnerId === 'string' && selectedPartnerId !== NO_PARTNER_VALUE ? selectedPartnerId : null;
  return selected === (storedPartnerId ?? null) ? {} : { partnerId: selected };
}
